import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

/**
 * Renders a single dynamic field's input based on its definition.
 * Controlled: pass `value` + `onChange(newValue)`.
 */
export default function DynamicFieldInput({ def, value, onChange, error }) {
  const { key, field_type, placeholder, options = [] } = def;
  const testId = `dyn-field-${key}`;
  const invalid = !!error;
  const cls = `mt-1 ${invalid ? "border-rose-400 focus-visible:ring-rose-400" : ""}`;

  if (field_type === "textarea") {
    return <Textarea data-testid={testId} value={value || ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className={cls} rows={3} />;
  }
  if (field_type === "number") {
    return <Input data-testid={testId} type="number" value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className={cls} />;
  }
  if (field_type === "date") {
    return <Input data-testid={testId} type="date" value={value || ""}
      onChange={(e) => onChange(e.target.value)} className={cls} />;
  }
  if (field_type === "checkbox") {
    return (
      <div className="flex items-center gap-2 mt-1.5">
        <Switch data-testid={testId} checked={!!value} onCheckedChange={onChange} />
        <span className="text-sm text-slate-500">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (field_type === "dropdown") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={cls}><SelectValue placeholder={placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field_type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div data-testid={testId} className={`flex flex-wrap gap-1.5 mt-1.5 ${invalid ? "ring-1 ring-rose-400 rounded-md p-1.5" : ""}`}>
        {options.map((o) => {
          const on = arr.includes(o.value);
          return (
            <button key={o.value} type="button" onClick={() => toggle(o.value)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  // text / phone / fallback
  return <Input data-testid={testId} value={value || ""} placeholder={placeholder}
    type={field_type === "phone" ? "tel" : "text"}
    onChange={(e) => onChange(e.target.value)} className={cls} />;
}

/** A labelled field group (label + required marker + input + help/error text). */
export function DynamicFieldGroup({ def, value, onChange, error }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-600">
        {def.label}
        {def.required && <span className="text-rose-500 ml-0.5">*</span>}
      </Label>
      <DynamicFieldInput def={def} value={value} onChange={onChange} error={error} />
      {error
        ? <p className="text-[11px] text-rose-500 mt-1">{error}</p>
        : def.help_text ? <p className="text-[11px] text-slate-400 mt-1">{def.help_text}</p> : null}
    </div>
  );
}
